import { useUpdatePart } from '@/core/domain/operational/Parts/Parts.hooks';
import { PartFormData } from '@/components/Operational/Inventory/Parts/PartForm/index';
import { useDefaultFormErrorNotification } from '@/hooks';
import { Form } from 'antd';
import { AxiosError } from 'axios';
import { Props } from 'framer-motion/types/types';
import { FormChangeInfo } from 'rc-field-form/es/FormContext';
import { createContext, useContext, useEffect, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import {
  PartFlag,
  UpdatePartRequest,
} from '@/core/domain/operational/Parts/Parts.types';
import { getImgUrl } from '@/core/domain/archive/Archive/Archive.utils';

interface Forms {
  generalParts?: PartFormData;
}

interface ContextValues {
  disabled: boolean;
  contextForm: Forms;
  isLoading: boolean;
  partId: string;
  setActive: (isActive: boolean) => void;
  handleSubmit: () => void;
  active: boolean;
}
const InventoryPartFormContext = createContext({} as ContextValues);
export const useInventoryPartFormContext = () =>
  useContext(InventoryPartFormContext);

const InventoryPartFormProvider: React.FC<Props> = ({ children }) => {
  const { sendFormErrorNotification } = useDefaultFormErrorNotification();
  const { partId } = useRouteMatch<{ partId: string }>().params;
  const [contextForm, setContextForm] = useState<Forms>({});
  const [isLoading, setIsLoading] = useState(false);
  const [active, setActive] = useState<boolean>(true);

  const updatePart = useUpdatePart();

  const disabled = !Object.keys(contextForm).some((key) => !!key);

  useEffect(() => {
    setContextForm({});
  }, [partId]);

  function handleFormChange(name: string, info: FormChangeInfo) {
    if (name && info?.forms[name]) {
      setContextForm((state) => ({
        ...state,
        [name]: info.forms[name].getFieldsValue(),
      }));
    }
  }

  async function handleSubmit() {
    setIsLoading(true);
    try {
      if (contextForm.generalParts) {
        const values = contextForm.generalParts;
        const obj: UpdatePartRequest = {
          ...values,
          active,
          id: +partId,
          flag: PartFlag.newPart,
          imageUrl: values?.imageUrl[0]
            ? getImgUrl(values.imageUrl[0])
            : undefined,
        };
        await updatePart.mutateAsync(obj);
      }
    } catch (error: unknown) {
      const err = error as AxiosError;
      if (!err.isAxiosError) {
        sendFormErrorNotification(error);
      }
    }
    setIsLoading(false);
    setContextForm({});
  }
  return (
    <InventoryPartFormContext.Provider
      value={{
        isLoading,
        contextForm,
        partId,
        disabled,
        handleSubmit,
        setActive,
        active,
      }}
    >
      <Form.Provider
        onFormChange={handleFormChange}
        onFormFinish={handleSubmit}
      >
        {children}
      </Form.Provider>
    </InventoryPartFormContext.Provider>
  );
};
export default InventoryPartFormProvider;
